// src/pages/dashboard/PaymentsPage.tsx
import React, { useState } from "react";
import {
  CreditCard,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  X,
  Mail,
} from "lucide-react";
import {
  useGetAdminOrdersQuery,
  useUpdateAdminOrderMutation,
  AdminOrder,
} from "../../app/api/adminApi";

function cn(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}
const naira = (n?: number) => `₦${Number(n || 0).toLocaleString()}`;
const fmtDate = (d?: string) =>
  d
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(d))
    : "—";

const STATUS_STYLES: Record<string, string> = {
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  pending: "border-amber-200 bg-amber-50 text-amber-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

const statusTabs = ["all", "paid", "pending", "failed", "cancelled"];

const OrderDrawer: React.FC<{ order: AdminOrder; onClose: () => void }> = ({
  order,
  onClose,
}) => {
  const [update, { isLoading }] = useUpdateAdminOrderMutation();
  const [note, setNote] = useState("");

  const act = async (patch: Partial<{ status: string; fulfilled: boolean; note: string }>) => {
    try {
      await update({ id: order._id, ...patch }).unwrap();
      if (patch.note) setNote("");
    } catch (e) {
      /* surfaced by RTK error state; keep drawer open */
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="font-semibold text-slate-900">Order detail</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-2xl font-bold text-slate-900">{naira(order.amount)}</p>
            <span
              className={cn(
                "mt-1 inline-block rounded-full border px-2 py-0.5 text-xs font-semibold",
                STATUS_STYLES[order.status]
              )}
            >
              {order.status}
            </span>
          </div>

          <dl className="space-y-2 text-sm">
            {[
              ["Customer", order.name || "—"],
              ["Email", order.email || "—"],
              ["Reference", order.paystackReference || "—"],
              ["Fulfilled", order.fulfilled ? "Yes" : "No"],
              ["Created", fmtDate(order.createdAt)],
              ["Paid at", fmtDate(order.paidAt)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-slate-50 py-1.5">
                <dt className="text-slate-500">{k}</dt>
                <dd className="text-right font-medium text-slate-800 break-all">{v}</dd>
              </div>
            ))}
          </dl>

          {order.email && (
            <a
              href={`mailto:${order.email}`}
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Mail className="h-4 w-4" /> Email customer
            </a>
          )}

          {/* manual reconciliation */}
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Manual actions
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                disabled={isLoading || order.status === "paid"}
                onClick={() => act({ status: "paid", fulfilled: true })}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
              </button>
              <button
                disabled={isLoading || order.status === "cancelled"}
                onClick={() => act({ status: "cancelled" })}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-40"
              >
                <XCircle className="h-3.5 w-3.5" /> Cancel
              </button>
              {!order.fulfilled && (
                <button
                  disabled={isLoading}
                  onClick={() => act({ fulfilled: true })}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-40"
                >
                  Mark fulfilled
                </button>
              )}
            </div>

            <div className="mt-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Add an internal note…"
                className="w-full rounded-xl border border-slate-200 p-2 text-sm outline-none focus:border-violet-300"
              />
              <button
                disabled={isLoading || !note.trim()}
                onClick={() => act({ note: note.trim() })}
                className="mt-2 w-full rounded-xl bg-slate-900 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                Save note
              </button>
            </div>

            {Array.isArray(order.metadata?.adminNotes) &&
              order.metadata.adminNotes.length > 0 && (
                <div className="mt-4 space-y-2">
                  {order.metadata.adminNotes.map((n: any, i: number) => (
                    <div key={i} className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                      <p>{n.note}</p>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {n.by} · {fmtDate(n.at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentsPage: React.FC = () => {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminOrder | null>(null);
  const limit = 25;

  const { data, isLoading, isFetching } = useGetAdminOrdersQuery({
    status,
    q: q.trim() || undefined,
    page,
    limit,
  });

  const orders = data?.data?.orders || [];
  const pagination = data?.data?.pagination;

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900">
          <CreditCard className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Payments
          </h1>
          <p className="text-sm text-slate-500">
            Monitor and reconcile every order across the platform.
          </p>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-400" />
        )}
      </div>

      {/* filters */}
      <div className="mb-5 flex flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name, email or reference…"
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm shadow-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {statusTabs.map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
              className={cn(
                "rounded-xl border px-3.5 py-1.5 text-xs font-semibold capitalize transition",
                status === s
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* table */}
      {isLoading ? (
        <div className="flex justify-center py-20 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Fulfilled</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.length ? (
                  orders.map((o) => (
                    <tr
                      key={o._id}
                      onClick={() => setSelected(o)}
                      className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{o.name || "—"}</p>
                        <p className="text-xs text-slate-400">{o.email}</p>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800">
                        {naira(o.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-xs font-semibold",
                            STATUS_STYLES[o.status]
                          )}
                        >
                          {o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {o.fulfilled ? (
                          <span className="text-emerald-600">Yes</span>
                        ) : (
                          <span className="text-slate-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {fmtDate(o.createdAt)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-slate-400">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <span className="text-sm text-slate-600">
            Page <b>{pagination.page}</b> / {pagination.totalPages}
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {selected && (
        <OrderDrawer order={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default PaymentsPage;
